import { TpDataContext } from '../../../data/interfaces';
import { TpWidgetContext } from '../dashboard/interfaces';
import { TpChartConfigParams, TpChartDataBinding } from './chartDataBindings';

export enum CONFIGPARAM_VISIBILITYCONDITION_TYPES {
  FACET_BOUND = 'facetBound',
  FACET_UNBOUND = 'facetUnBound',
  CONFIG_VALUE = 'configValue',
  CONFIG_ABSENT = 'configAbsent',
  CONFIG_PRESENT = 'configPresent',
  ANY_CONFIG_PRESENT = 'anyConfigPresent',
}

export interface TpChartManifestAspectDef {
  id: string;
  name: string;
  description?: string;
  groupId: string;
  required?: boolean;
  hidden?: boolean; // if true, this aspect is not visible for the end user
  customAbsentLabel?: string; // custom diaplay name used in case this aspect is not bound
  quality?: number; // determines the quality of visualisation, between 0 and 1 (example: hover labels = quality 0)
  dataType: any;
  onColumnItems?: boolean; // if true, this aspect must be bound to the datatable containing the items that the columns refer to (i.e. for a data matrix)
  documentation?: TpDocViewerElement[] | TpDocViewerElementGenerator; // documentation for the aspect
}

export interface TpChartManifestCompoundAspectDef {
  // defines aspect that are bound to a group of columns
  id: string;
  name: string;
  description?: string;
  groupId: string;
  required?: boolean;
  hidden?: boolean; // if true, this aspect is not visible for the end user
  dataType: any;
  minCount: number;
  documentation?: TpDocViewerElement[] | TpDocViewerElementGenerator; // documentation for the aspect
}

export interface TpChartManifestConfigParamDef {
  id: string;
  name: string;
  description?: string;
  groupId: string;
  paramType: any; // in case this is a function => this function will be called dynamically to fetch the parameter type, providing the currently bound data
  hidden?: boolean;
  documentation?: TpDocViewerElement[] | TpDocViewerElementGenerator; // documentation for the parameter
  visibilityConditions?: {
    conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES;
    conditionInfo: any;
  }[];
}

export interface TpChartManifest {
  id: string;
  name: string;
  description?: string;
  showDataMatrix?: boolean; // if true, chart show a data matrix (e.g. heat map), needing another data table to be linked to a set some of its columns, identified by their column type in the chart definition prop columnsCanonicalTyped
  dataMatrixDocumentationGroup?: string; // groupId that should contain documentation on the dataMatrix, if present.
  groups: { groupId: string; groupName: string }[];
  aspects: TpChartManifestAspectDef[];
  compoundAspects?: TpChartManifestCompoundAspectDef[];
  secondTable?: {
    // used in case a second table is involved (e.g. holding data points for longitudinal data)
    aspects: TpChartManifestAspectDef[];
  };
  configParams: TpChartManifestConfigParamDef[];

  component: any;
  canvasBased: boolean;
  examples?: {
    id: string;
  }[];
}

// Props of an instantiated chart react component
export interface TpChartProps {
  chartTypeManifest: TpChartManifest;
  dataContext: TpDataContext;
  dataBinding: TpChartDataBinding;
  config: TpChartConfigParams;
  widgetContext: TpWidgetContext;
}

// Widget doc viewer types
export enum DOCVIEWER_ELEMENT_TYPES {
  STRING = 'string', // string or markdown string
  PROPERTY = 'property', // mapping of a property
  PROPERTIES = 'properties', // list of properties
}

export interface TpMarkdownDocViewerElement {
  type: DOCVIEWER_ELEMENT_TYPES.STRING;
  id: string;
  content: string;
}

export type TpDocViewerPropertyDescription = {
  name: string;
  description: string | null;
};

export interface TpPropertyDocViewerElement {
  type: DOCVIEWER_ELEMENT_TYPES.PROPERTY;
  id: string;
  content: TpDocViewerPropertyDescription;
}

export interface TpPropertiesDocViewerElement {
  type: DOCVIEWER_ELEMENT_TYPES.PROPERTIES;
  id: string;
  content: TpDocViewerPropertyDescription[];
}

// Discriminating union type for the doc viewer elements
export type TpDocViewerElement = TpMarkdownDocViewerElement | TpPropertyDocViewerElement | TpPropertiesDocViewerElement;

export type TpDocViewerElementGenerator = (
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) => TpDocViewerElement[];
